import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { api } from "../lib/api";
import {
  ArrowRight, Search, Target, Sparkles, ShieldCheck, Code2,
  Briefcase, Building2, Brain, FileText, ChevronRight, Zap, LayoutDashboard,
} from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({ jobs: 0, candidates: 0, employers: 0 });
  useDocumentTitle("Home");

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const popularSkills = [
    "Selenium", "Playwright", "Cypress", "API Testing", "JMeter",
    "TestNG", "Appium", "REST Assured", "CI/CD", "Robot Framework",
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid pointer-events-none" />
        <div className="absolute top-32 -right-32 h-96 w-96 rounded-full bg-[#002FA7]/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-200 bg-white text-xs font-medium text-zinc-700 mb-6">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="tag-mono">{stats.jobs}+ open QA roles</span>
                <span className="text-zinc-300">·</span>
                <span>built for testers, by testers</span>
              </div>

              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-zinc-950">
                The career platform for{" "}
                <span className="brand-gradient-text">QA & Automation</span> talent.
              </h1>

              <p className="mt-6 text-lg text-zinc-600 max-w-xl leading-relaxed">
                QaTestology connects software testers, SDETs and QA engineers with employers
                who need them. Get an AI-powered skill gap analysis, build a sharp profile,
                and let recruiters find you — not the other way around.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                {user ? (
                  <>
                    <Button
                      size="lg"
                      data-testid="hero-browse-jobs-cta"
                      onClick={() => navigate("/jobs")}
                      className="bg-[#002FA7] hover:bg-[#00227A] text-white h-12 px-6 text-base"
                    >
                      <Search className="mr-2 h-4 w-4" /> Browse jobs
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      data-testid="hero-dashboard-cta"
                      onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
                      className="h-12 px-6 text-base border-zinc-300"
                    >
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      {user.role === "admin" ? "Admin panel" : "My dashboard"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="lg"
                      data-testid="hero-seeker-cta"
                      onClick={() => navigate("/signup?role=job_seeker")}
                      className="bg-[#002FA7] hover:bg-[#00227A] text-white h-12 px-6 text-base"
                    >
                      I'm a tester — Find jobs <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      data-testid="hero-employer-cta"
                      onClick={() => navigate("/signup?role=employer")}
                      className="h-12 px-6 text-base border-zinc-300"
                    >
                      I'm hiring — Find QA talent
                    </Button>
                  </>
                )}
              </div>

              <div className="mt-10 flex items-center gap-6 text-sm text-zinc-500">
                <div>
                  <div className="font-display text-2xl font-bold text-zinc-950">{stats.candidates}+</div>
                  <div className="tag-mono uppercase tracking-widest text-xs">testers</div>
                </div>
                <div className="h-10 w-px bg-zinc-200" />
                <div>
                  <div className="font-display text-2xl font-bold text-zinc-950">{stats.employers}+</div>
                  <div className="tag-mono uppercase tracking-widest text-xs">employers</div>
                </div>
                <div className="h-10 w-px bg-zinc-200" />
                <div>
                  <div className="font-display text-2xl font-bold text-zinc-950">{stats.jobs}+</div>
                  <div className="tag-mono uppercase tracking-widest text-xs">live jobs</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              {/* AI Skill card mock */}
              <div className="relative bg-white border border-zinc-200 rounded-xl shadow-[0_30px_80px_-20px_rgba(0,47,167,0.18)] p-6 fade-up">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#002FA7]" />
                    <span className="text-xs font-semibold uppercase tracking-widest text-[#002FA7]">AI Skill Gap</span>
                  </div>
                  <span className="ai-score-badge">82 / 100</span>
                </div>
                <div className="font-display text-2xl font-bold tracking-tight text-zinc-950 mb-1">
                  Automation QA Engineer
                </div>
                <div className="text-sm text-zinc-500 mb-5">Personalized for your profile</div>

                <div className="space-y-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-emerald-700 mb-1.5">strengths</div>
                    <div className="flex flex-wrap gap-1.5">
                      {["Selenium", "Java", "JUnit", "Git"].map(s => (
                        <span key={s} className="tag-mono px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded">{s}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-amber-700 mb-1.5">add these</div>
                    <div className="flex flex-wrap gap-1.5">
                      {["Playwright", "REST Assured", "CI/CD", "Docker"].map(s => (
                        <span key={s} className="tag-mono px-2 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded">{s}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-5 pt-5 border-t border-zinc-100 flex items-center justify-between text-sm">
                  <span className="text-zinc-500">Match potential after 90d learning</span>
                  <span className="font-mono font-bold text-emerald-600">+15%</span>
                </div>
              </div>

              <div className="absolute -top-4 -left-4 -z-10 h-24 w-24 rounded-full bg-amber-100 blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* SKILL MARQUEE */}
      <section className="border-y border-zinc-200 bg-zinc-50/60 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-center gap-2">
          <span className="text-xs uppercase tracking-widest text-zinc-500 mr-3">popular skills:</span>
          {popularSkills.map((s) => (
            <Badge key={s} variant="outline" className="tag-mono bg-white border-zinc-200 text-zinc-700">
              {s}
            </Badge>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-3">how it works</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-950">
              One platform. Two sides. Zero noise.
            </h2>
            <p className="mt-4 text-zinc-600">
              Whether you're chasing your next role or your next hire, QaTestology cuts through generic
              job boards with QA-specific signal.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="border border-zinc-200 rounded-md p-8 hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)] transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-md bg-blue-50 grid place-items-center text-[#002FA7]">
                  <Briefcase className="h-5 w-5" />
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight">For job seekers</h3>
              </div>
              <ul className="space-y-3 text-sm text-zinc-700">
                {[
                  ["Build a tester profile", "Skills, tools, certifications, resume — in 5 minutes."],
                  ["AI Skill Gap Analyzer", "See exactly which skills will get you hired faster."],
                  ["One-click apply", "Apply to QA roles tailored to your stack."],
                ].map(([t, d]) => (
                  <li key={t} className="flex gap-3">
                    <ChevronRight className="h-4 w-4 mt-0.5 text-[#002FA7] flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-zinc-950">{t}</div>
                      <div className="text-zinc-600">{d}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <Button
                variant="ghost"
                className="mt-6 px-0 hover:bg-transparent text-[#002FA7] hover:text-[#00227A]"
                onClick={() => navigate(user ? "/jobs" : "/signup?role=job_seeker")}
                data-testid="feature-seeker-cta"
              >
                {user ? "Browse jobs" : "Create a tester profile"} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>

            <div className="border border-zinc-200 rounded-md p-8 bg-zinc-950 text-white hover:shadow-[0_8px_32px_rgba(0,47,167,0.25)] transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-md bg-white/10 grid place-items-center text-white">
                  <Building2 className="h-5 w-5" />
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight">For employers</h3>
              </div>
              <ul className="space-y-3 text-sm text-zinc-300">
                {[
                  ["Post QA roles", "Reach only people who actually test software."],
                  ["AI candidate matching", "Every applicant ranked by a skill-match score."],
                  ["Browse vetted profiles", "Skills, tools, experience — searchable & filterable."],
                ].map(([t, d]) => (
                  <li key={t} className="flex gap-3">
                    <ChevronRight className="h-4 w-4 mt-0.5 text-emerald-400 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-white">{t}</div>
                      <div className="text-zinc-400">{d}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <Button
                className="mt-6 bg-white text-zinc-950 hover:bg-zinc-100"
                onClick={() => navigate(user?.role === "employer" ? "/employer/post-job" : (user ? "/jobs" : "/signup?role=employer"))}
                data-testid="feature-employer-cta"
              >
                {user?.role === "employer" ? "Post a job" : (user ? "Browse jobs" : "Post your first job")} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* AI HIGHLIGHT */}
      <section className="py-20 lg:py-28 bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-2 py-1 rounded bg-zinc-950 text-emerald-400 text-xs font-mono mb-4">
              <Brain className="h-3 w-3" /> POWERED BY CLAUDE SONNET 4.5
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-950">
              An AI coach that knows<br />the QA market.
            </h2>
            <p className="mt-4 text-zinc-600 max-w-lg">
              Tell us your target role and what you already know. Our AI returns a personalized
              match score, ranked missing skills, and a 4–6 step learning path — so you can
              prioritise what to learn next.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Match score", "Strengths", "Missing skills", "Learning path"].map(t => (
                <Badge key={t} className="bg-white border border-zinc-200 text-zinc-800 tag-mono">{t}</Badge>
              ))}
            </div>
            <Button
              className="mt-8 bg-[#002FA7] hover:bg-[#00227A] text-white"
              onClick={() => navigate("/signup?role=job_seeker")}
              data-testid="ai-cta"
            >
              Run my skill gap analysis
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { i: Code2, t: "Frameworks", v: "Selenium · Cypress · Playwright" },
              { i: Zap, t: "Performance", v: "JMeter · K6 · LoadRunner" },
              { i: ShieldCheck, t: "Certifications", v: "ISTQB · CSTE" },
              { i: FileText, t: "Reports & CI", v: "Allure · Jenkins · GHA" },
            ].map(({ i: Icon, t, v }) => (
              <div key={t} className="bg-white border border-zinc-200 rounded-md p-5">
                <Icon className="h-5 w-5 text-[#002FA7] mb-3" />
                <div className="font-display font-semibold text-zinc-950">{t}</div>
                <div className="tag-mono text-zinc-500 mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-950">
            Ready to make testing your career advantage?
          </h2>
          <p className="mt-4 text-zinc-600">
            Free to join. No spam, no recruiters in your inbox unless they actually have a role for you.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            {user ? (
              <Button
                size="lg"
                className="bg-[#002FA7] hover:bg-[#00227A] text-white h-12 px-6"
                onClick={() => navigate("/jobs")}
                data-testid="bottom-cta-browse-jobs"
              >
                <Search className="mr-2 h-4 w-4" /> Browse jobs
              </Button>
            ) : (
              <>
                <Button
                  size="lg"
                  className="bg-[#002FA7] hover:bg-[#00227A] text-white h-12 px-6"
                  onClick={() => navigate("/signup")}
                  data-testid="bottom-cta-signup"
                >
                  Get started — it's free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 px-6 border-zinc-300"
                  onClick={() => navigate("/jobs")}
                  data-testid="bottom-cta-jobs"
                >
                  <Search className="mr-2 h-4 w-4" /> Browse jobs
                </Button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-zinc-200 py-10 pb-24 sm:pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-4 sm:pr-40">
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-md bg-[#002FA7] text-white grid place-items-center">
              <Target className="h-3.5 w-3.5" strokeWidth={2.5} />
            </span>
            <span className="font-display font-bold text-zinc-950">Qa<span className="text-[#002FA7]">Testology</span></span>
          </div>
          <div className="text-xs text-zinc-500 tag-mono text-center sm:text-left" data-testid="footer-copyright">
            © {new Date().getFullYear()} qatestology.com · built for testers
          </div>
        </div>
      </footer>
    </div>
  );
}
